/**
 * CESP test publisher: entry point.
 *
 * Reads configuration from URL params, renders the declared layout,
 * optionally simulates bloat, boots the CMP, and hands video playback over
 * to IMA SDK + OM SDK Web Video.
 *
 * URL params (all optional):
 *   vast          : VAST tag URL (the ad server to audit)
 *   content       : HTML5 video content URL (defaults to a short public sample)
 *   contentType   : text | text-image | text-image-video (default: text-image-video)
 *   videoPos      : CSS position like "top-left" | "center" | "bottom-right" (default: center)
 *   videoSize     : 25 | 50 | 75 | 100 (percent width, default: 75)
 *   videoRatio    : 16:9 | 4:3 (default: 16:9)
 *   pageHeight    : percentage of viewport height (default: 100 -> single screen)
 *   pageWidth     : percentage of viewport width (default: 100)
 *   bloat         : none | low | high  (injects dummy assets / scripts)
 *   consent       : granted | refused | absent (driven by cmp.js)
 *   adFormat      : pre-roll | mid-roll | outstream | overlay (default: pre-roll)
 *   autoplay      : 1 | 0 (default: 1)
 *   muted         : 1 | 0 (default: 1 for autoplay compatibility)
 *   imageContent  : image URL from layout upload (optional)
 *
 * --- MRC viewability pinning (CESP-685) ---
 *   visible       : 0–100  integer percentage of the player area that must sit
 *                   inside the viewport. When set, renderLayout() switches
 *                   .player-wrap to position:fixed and pins it via
 *                   getBoundingClientRect() so the fraction is pixel-exact.
 *                   Omitting this param keeps the normal scroll-based layout.
 *   cut           : bottom | top | left | right  (default: bottom)
 *                   Which viewport edge cuts the player. Combined with
 *                   `visible` to determine the sign and axis of the offset.
 *
 * Everything the runner needs is surfaced on window.__CESP_TEST_APP__ for
 * deterministic Playwright interaction.
 */

import { applyBadgeInset, createBadgeController } from './adBadge.js';
import { DEFAULT_AD_SIZE, browserObserver, createAdSizeSync, readElementSize } from './adSizing.js';
import { setupCmp } from './cmp.js';
import { createImaContentSync } from './imaContentSync.js';
import { createApplyMuted, createApplyVolume } from './imaVolume.js';
// Only bootstrap OM SDK manually when window.google.ima is absent (e.g., Tizen).
import { bootstrapOmsdk } from './omsdk.js';
import { createContentPlayback } from './playback.js';

const DEFAULT_CONTENT = 'https://storage.googleapis.com/gvabox/media/samples/stock.mp4';
// `single_preroll_skippable` serves <Linear skipoffset="00:00:05"> so IMA renders a Skip button;
// the previous `single_ad_samples` unit declares no skipoffset and is never skippable.
const DEFAULT_VAST =
  'https://pubads.g.doubleclick.net/gampad/ads?iu=/21775744923/external/single_preroll_skippable&sz=640x480&ciu_szs=300x250%2C728x90&gdfp_req=1&output=vast&unviewed_position_start=1&env=vp&impl=s&correlator=';

function withFreshRandomCachebuster(url) {
  return url.replace(/\{random\}/gi, () => String(Math.floor(Math.random() * 1_000_000_000)));
}

/**
 * Read URL params into a normalised config object. Missing values keep
 * their sensible defaults so the page still renders when loaded manually.
 */
function readConfig() {
  const p = new URLSearchParams(window.location.search);

  // MRC viewability pinning (CESP-685).
  // `visible` is null when the param is absent → normal scroll-based layout.
  // When present, value is clamped to [0, 100] after parsing.
  const rawVisible = p.get('visible');
  const visible =
    rawVisible !== null ? Math.max(0, Math.min(100, Number.parseInt(rawVisible, 10))) : null;

  /** @type {'bottom'|'top'|'left'|'right'} */
  const cut = ['bottom', 'top', 'left', 'right'].includes(p.get('cut') ?? '')
    ? /** @type {'bottom'|'top'|'left'|'right'} */ (p.get('cut'))
    : 'bottom';

  return {
    vast: p.get('vast') || DEFAULT_VAST,
    content: p.get('content') || DEFAULT_CONTENT,
    contentType: p.get('contentType') || 'text-image-video',
    videoPos: p.get('videoPos') || 'center',
    videoSize: Number.parseInt(p.get('videoSize') ?? '75', 10),
    videoRatio: p.get('videoRatio') || '16:9',
    pageHeight: Number.parseInt(p.get('pageHeight') ?? '100', 10),
    pageWidth: Number.parseInt(p.get('pageWidth') ?? '100', 10),
    bloat: p.get('bloat') || 'none',
    consent: p.get('consent') || 'absent',
    adFormat: p.get('adFormat') || 'pre-roll',
    autoplay: p.get('autoplay') !== '0',
    muted: p.get('muted') !== '0',
    imageContent: p.get('imageContent') || null,
    customText: p.get('customText') || null,
    // MRC pinning
    visible,
    cut,
  };
}

/**
 * Apply layout variables: page dimensions in % of viewport, video position,
 * video size and aspect ratio. Keeps DOM structure identical across
 * layouts so Playwright selectors stay stable.
 *
 * When `config.visible` is set (non-null), the player is switched to
 * `position:fixed` and pinned so that exactly `visible`% of its area is
 * inside the viewport, cut at the edge specified by `config.cut`.
 * This is pixel-deterministic (uses getBoundingClientRect) and is the
 * mechanism behind SmartClip MRC scenarios T1–T8, T13 (CESP-685).
 */
function renderLayout(config) {
  const root = document.getElementById('page-root');
  root.style.minHeight = `${config.pageHeight}vh`;
  root.style.width = `${config.pageWidth}vw`;

  const wrap = document.querySelector('.player-wrap');
  wrap.style.width = `${config.videoSize}%`;
  wrap.style.aspectRatio = config.videoRatio.replace(':', ' / ');
  wrap.style.marginLeft = 'auto';
  wrap.style.marginRight = 'auto';

  // Stash videoSize fraction as a data attribute so applyFixedPositioning
  // can recover the intended width without re-parsing CSS (which would be
  // unreliable once position:fixed changes the containing block).
  wrap.dataset.cesVideoSizePct = String(config.videoSize / 100);

  const [vy, vx] =
    config.videoPos.split('-').length === 2 ? config.videoPos.split('-') : ['center', 'center'];
  wrap.style.alignSelf = mapAlign(vy);
  wrap.style.marginTop = vy === 'top' ? '0' : vy === 'bottom' ? 'auto' : '32px';
  wrap.style.marginBottom = vy === 'bottom' ? '0' : '16px';
  if (vx === 'left') wrap.style.marginLeft = '0';
  if (vx === 'right') wrap.style.marginRight = '0';

  // Content rails around the player. Fills out a realistic publisher
  // page when contentType demands text/image.
  const contentLayout = document.getElementById('content-layout');
  contentLayout.innerHTML = '';
  if (config.imageContent && config.contentType.includes('image')) {
    const imgWrap = document.createElement('div');
    imgWrap.className = 'card';
    imgWrap.id = 'image-block';
    const img = document.createElement('img');
    img.src = config.imageContent;
    img.alt = 'layout image';
    img.style.width = '100%';
    img.style.maxHeight = '240px';
    img.style.objectFit = 'cover';
    img.style.borderRadius = '6px';
    imgWrap.appendChild(img);
    contentLayout.appendChild(imgWrap);
  }
  if (config.contentType.includes('text') || config.contentType.includes('image')) {
    if (config.customText) {
      const card = document.createElement('div');
      card.className = 'card';
      card.id = 'custom-text-block';
      const h2 = document.createElement('h2');
      h2.textContent = 'Custom Text';
      const p = document.createElement('p');
      p.textContent = config.customText;
      p.style.whiteSpace = 'pre-wrap';
      p.style.wordBreak = 'break-word';
      p.style.color = 'var(--fg)';
      card.appendChild(h2);
      card.appendChild(p);
      contentLayout.appendChild(card);
    }
    for (let i = 0; i < 6; i++) {
      const card = document.createElement('div');
      card.className = 'card';
      card.innerHTML = `<h2>Series ${i + 1}</h2><p>Lorem ipsum dolor sit amet, consectetur adipiscing elit. Catalogue row ${i + 1} simulates a realistic OTT home page.</p>`;
      contentLayout.appendChild(card);
    }
  }

  // MRC viewability pinning (CESP-685).
  // Guard: only activate when the `visible` param is explicitly set.
  // Scroll-based scenarios (no `visible` param) are completely unaffected.
  if (config.visible !== null) {
    applyFixedPositioning(wrap, config.visible, config.cut);
  }
}

function mapAlign(pos) {
  if (pos === 'top') return 'flex-start';
  if (pos === 'bottom') return 'flex-end';
  return 'center';
}

/**
 * Switch `.player-wrap` to `position:fixed` and offset it so that exactly
 * `visiblePct`% of its area sits inside the viewport, cut at `cut` edge.
 *
 * Design rationale:
 *   - `position:fixed` removes the element from document flow, so the
 *     position is purely viewport-relative and unaffected by scroll.
 *   - We use getBoundingClientRect() after setting explicit dimensions so
 *     the computed size matches what the measurer will see (same API).
 *   - The child `#ad-container` is `position:absolute; inset:0` relative
 *     to `.player-wrap`, which remains its containing block even when
 *     fixed — no changes needed to the IMA container.
 *
 * Geometry per edge (all values in CSS pixels):
 *
 *   cut=bottom  top  = innerHeight − (visiblePct/100 × playerHeight)
 *               Positive top → player starts below the fold by the hidden amount.
 *
 *   cut=top     top  = −(playerHeight × (1 − visiblePct/100))
 *               Negative top → player overflows above the viewport.
 *
 *   cut=right   left = innerWidth  − (visiblePct/100 × playerWidth)
 *   cut=left    left = −(playerWidth  × (1 − visiblePct/100))
 *
 * After pinning, the actual measured fraction is logged to #diag and
 * exposed on window.__CESP_TEST_APP__ for Playwright assertions.
 *
 * @param {HTMLElement} wrap        - The .player-wrap element.
 * @param {number}      visiblePct  - Desired visible percentage [0, 100].
 * @param {'bottom'|'top'|'left'|'right'} cut - Viewport edge doing the cut.
 */
function applyFixedPositioning(wrap, visiblePct, cut) {
  const fraction = visiblePct / 100;

  // Step 1: Switch to fixed positioning. Set explicit pixel dimensions so
  // getBoundingClientRect reflects the real rendered size immediately.
  // Without explicit width/height, aspect-ratio CSS may not resolve until
  // after the element is painted, giving us a 0×0 rect.
  wrap.style.position = 'fixed';
  // Remove flow-layout margins that would fight with top/left offset.
  wrap.style.margin = '0';
  wrap.style.alignSelf = '';

  // Step 2: Compute explicit pixel dimensions.
  // Width: percentage of viewport width (same semantics as before, but now
  // relative to the viewport because the element is fixed-positioned).
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  // Respect the existing videoSize percentage (default 75% of container width).
  // For fixed-positioned elements the containing block IS the viewport.
  // We read back the stashed fraction (set by renderLayout before calling us).
  const sizePct = Number.parseFloat(wrap.dataset.cesVideoSizePct ?? '0.75');
  const playerWidthPx = Math.round(vw * sizePct);
  // Compute height from aspect ratio. The wrap already has aspect-ratio set
  // via CSS; we derive the numeric ratio from the style string (e.g. "16 / 9").
  const aspectStyle = wrap.style.aspectRatio || '16 / 9';
  const [arW, arH] = aspectStyle.split('/').map((s) => Number.parseFloat(s.trim()));
  const playerHeightPx = Math.round(playerWidthPx * ((arH || 9) / (arW || 16)));

  wrap.style.width = `${playerWidthPx}px`;
  wrap.style.height = `${playerHeightPx}px`;

  // Step 3: Compute top / left offset so that `fraction` of the player
  // is visible at the specified edge.
  let topPx = 0;
  let leftPx = 0;

  switch (cut) {
    case 'bottom':
      // Player hangs off the bottom. The top edge of the player is
      // `fraction * height` pixels above the bottom of the viewport.
      topPx = vh - Math.round(fraction * playerHeightPx);
      leftPx = Math.round((vw - playerWidthPx) / 2); // horizontally centered
      break;

    case 'top':
      // Player hangs off the top. Negative top pushes it above the viewport.
      topPx = -Math.round((1 - fraction) * playerHeightPx);
      leftPx = Math.round((vw - playerWidthPx) / 2);
      break;

    case 'right':
      // Player hangs off the right edge.
      topPx = Math.round((vh - playerHeightPx) / 2); // vertically centered
      leftPx = vw - Math.round(fraction * playerWidthPx);
      break;

    case 'left':
      // Player hangs off the left edge.
      topPx = Math.round((vh - playerHeightPx) / 2);
      leftPx = -Math.round((1 - fraction) * playerWidthPx);
      break;
  }

  wrap.style.top = `${topPx}px`;
  wrap.style.left = `${leftPx}px`;
  // Clear right/bottom so they don't conflict with explicit top/left.
  wrap.style.right = 'auto';
  wrap.style.bottom = 'auto';

  // Step 3b: Re-anchor the playback-phase badge to the top-right of the VISIBLE
  // slice. `cut=top` / `cut=right` hide the player's own top-right corner, so
  // a badge left at the CSS margins is pinned off-frame and the evidence
  // video of that scenario carries no label at all (CESP-696).
  const badgeInset = applyBadgeInset(document.getElementById('ad-content-badge'), {
    playerWidth: playerWidthPx,
    playerHeight: playerHeightPx,
    visiblePct,
    cut,
  });

  // Step 4: Verify with getBoundingClientRect and log measured fraction.
  // This is the same API the measurer uses, so it confirms pixel-accuracy.
  const rect = wrap.getBoundingClientRect();
  const measuredFraction = computeMeasuredFraction(rect, vw, vh, cut);

  logDiag(
    `[MRC-pin] visible=${visiblePct}% cut=${cut} ` +
      `playerSize=${playerWidthPx}×${playerHeightPx}px ` +
      `top=${topPx}px left=${leftPx}px ` +
      `badgeInset=${badgeInset ? `${badgeInset.top}/${badgeInset.right}px` : 'none'} ` +
      `measuredFraction=${(measuredFraction * 100).toFixed(2)}%`,
  );

  // Expose for Playwright assertions (CESP-686).
  window.__CESP_VIEWABILITY__ = {
    visiblePct,
    cut,
    playerRect: {
      top: rect.top,
      left: rect.left,
      width: rect.width,
      height: rect.height,
      bottom: rect.bottom,
      right: rect.right,
    },
    measuredFraction,
  };
}

/**
 * Compute the fraction of `rect` that is inside the viewport [0, 1].
 * Mirrors the geometry a viewability measurer would compute.
 *
 * @param {DOMRect} rect
 * @param {number}  vw   window.innerWidth
 * @param {number}  vh   window.innerHeight
 * @param {'bottom'|'top'|'left'|'right'} cut
 * @returns {number}
 */
function computeMeasuredFraction(rect, vw, vh, cut) {
  if (cut === 'left' || cut === 'right') {
    // Measure horizontal fraction.
    const visibleLeft = Math.max(rect.left, 0);
    const visibleRight = Math.min(rect.right, vw);
    const visibleWidth = Math.max(0, visibleRight - visibleLeft);
    return rect.width > 0 ? visibleWidth / rect.width : 0;
  }
  // Measure vertical fraction (bottom / top cuts).
  const visibleTop = Math.max(rect.top, 0);
  const visibleBottom = Math.min(rect.bottom, vh);
  const visibleHeight = Math.max(0, visibleBottom - visibleTop);
  return rect.height > 0 ? visibleHeight / rect.height : 0;
}

/**
 * Inject artificial weight to simulate real publisher page load:
 *   - low  : ~500 KB of analytics-like scripts (stubbed)
 *   - high : ~2 MB of scripts + hidden assets
 */
function applyBloat(level) {
  if (level === 'none') return;
  const count = level === 'high' ? 20 : 5;
  for (let i = 0; i < count; i++) {
    const s = document.createElement('script');
    // Data URL so it doesn't hit the network but still takes time to parse.
    s.textContent = `window.__bloat${i} = ${JSON.stringify(new Array(20_000).fill('x').join(''))};`;
    document.head.appendChild(s);
  }
}

/**
 * Configure IMA SDK, set up the AdsLoader/AdsManager lifecycle, and wire
 * video events to the OM SDK bootstrap (omsdk.js).
 */
function setupImaSdk(config) {
  if (!window.google || !window.google.ima) {
    const message = 'IMA SDK failed to load. Check network or blocking.';
    logDiag(message);
    console.error(`[CESP] AD_ERROR: ${message}`);
    return;
  }
  const video = document.getElementById('content-video');
  const adContainer = document.getElementById('ad-container');
  const playback = createContentPlayback(video, config.autoplay);
  const imaContentSync = createImaContentSync(video, playback, config.adFormat);
  const badgePhase = createBadgeController(document.getElementById('ad-content-badge'));
  const adLoadingOverlay = document.getElementById('ad-loading-overlay');
  const isOverlay = config.adFormat === 'overlay';
  // The scrim ships visible in index.html, so the badge starts a run knowing
  // that it, and not the content element, is what covers the player.
  badgePhase.adLoadingChanged(true);

  // The content element is the only thing that knows content is really on
  // screen: IMA's resume cue fires before the first frame, and with
  // autoplay=0 nothing plays until the runner's gesture. Driving the badge
  // off `playing` covers both without a timer.
  video.addEventListener('playing', () => badgePhase.contentStarted());

  /** @param {string|null} errorMsg - null hides normally; string shows error state. */
  function hideLoadingOverlay(errorMsg) {
    // Even the error branch below leaves the player readable enough to label
    // what is on it, and content resumes underneath after an ad error.
    badgePhase.adLoadingChanged(false);
    if (!adLoadingOverlay) return;
    if (errorMsg) {
      adLoadingOverlay.classList.add('is-error');
      const label = adLoadingOverlay.querySelector('.cesp-overlay-label');
      if (label) label.textContent = errorMsg;
      // Keep overlay visible so the error is baked into the evidence video.
    } else {
      adLoadingOverlay.classList.add('hidden');
    }
  }

  const adDisplayContainer = new google.ima.AdDisplayContainer(adContainer, video);
  const adsLoader = new google.ima.AdsLoader(adDisplayContainer);

  /** @type {google.ima.AdsManager | null} */
  let adsManager = null;
  /** @type {ReturnType<typeof createAdSizeSync> | null} */
  let adSizeSync = null;
  const applyVolume = createApplyVolume(video, () => adsManager);
  const applyMuted = createApplyMuted(video, () => adsManager);

  adsLoader.addEventListener(
    google.ima.AdsManagerLoadedEvent.Type.ADS_MANAGER_LOADED,
    (event) => {
      const settings = new google.ima.AdsRenderingSettings();
      settings.restoreCustomPlaybackStateOnAdBreakComplete = true;
      adsManager = event.getAdsManager(video, settings);

      // IMA handles AdVerifications natively. Manual bootstrap causes double sessions.
      adsManager.addEventListener(
        google.ima.AdEvent.Type.STARTED,
        (e) => {
          try {
            const ad = e.getAd?.();
            const adId = ad?.getAdId?.() ?? 'unknown';
            logDiag(`Ad STARTED: adId=${adId}. IMA owns OMID session — skipping manual bootstrap.`);
            hideLoadingOverlay(null);
            // A nonlinear banner renders over content that never stopped
            // playing, so it must not take the badge off "Content". An ad IMA
            // will not describe is treated as linear: a missing "Ad" label is
            // the worse failure.
            if (!isOverlay && ad?.isLinear?.() !== false) {
              badgePhase.adStarted();
            }
            // Signal to PlaywrightRunner that IMA has actually started rendering
            // the ad (VAST loaded + SDK init complete). Used by the skip handler
            // to sync its timeline with real ad playback start, not page load.
            if (window.__CESP_TEST_APP__) {
              window.__CESP_TEST_APP__.adStarted = true;
            }
            // Surface ad metadata for Playwright assertions.
            window.__CESP_OMSDK__ = window.__CESP_OMSDK__ ?? {
              adSession: null,
              adEvents: null,
              videoEvents: null,
              verifications: [],
              managedByIma: true,
            };
          } catch (err) {
            logDiag(`Ad STARTED handler error: ${err.message}`);
          }
        },
        false,
      );

      adsManager.addEventListener(google.ima.AdEvent.Type.ALL_ADS_COMPLETED, () => {
        hideLoadingOverlay(null);
        badgePhase.adEnded();
        playback.playContent();
        // No ad UI left to keep in sync; drop the observer with it.
        adSizeSync?.stop();
      });
      adsManager.addEventListener(google.ima.AdEvent.Type.SKIPPED, () => {
        logDiag('Ad skipped by user action.');
        if (window.__CESP_TEST_APP__) {
          window.__CESP_TEST_APP__.adSkipped = true;
        }
      });
      // CESP-696: drive the Loading/Ad/Content badge so evidence video
      // annotators can tell a buffering player, an ad segment and publisher
      // content apart. IMA fires these around every ad break
      // (pre-roll/mid-roll/outstream), not just once.
      adsManager.addEventListener(google.ima.AdEvent.Type.CONTENT_PAUSE_REQUESTED, () => {
        // The cue only says the break is coming: IMA has the surface but has
        // not drawn a frame on it, so the badge waits for STARTED.
        if (!isOverlay) badgePhase.adBreakStarting();
        imaContentSync.onContentPauseRequested();
      });
      adsManager.addEventListener(google.ima.AdEvent.Type.CONTENT_RESUME_REQUESTED, () => {
        badgePhase.adEnded();
        imaContentSync.onContentResumeRequested();
      });

      try {
        // Measure the ad container (inset:0 over .player-wrap), not the video:
        // it is the surface IMA actually renders into, and it tracks the wrap
        // when the MRC-pin layouts resize it.
        const initialSize = readElementSize(adContainer) ??
          readElementSize(video) ?? { ...DEFAULT_AD_SIZE };
        adsManager.init(initialSize.width, initialSize.height, google.ima.ViewMode.NORMAL);
        adsManager.start();
        // IMA starts every break at full volume regardless of the content
        // element, so push the player's own level once init() has run. The
        // element's level, not a switch: a Mute/Unmute step that already ran
        // must not be undone by the next break (CESP-964).
        applyVolume(video.muted ? 0 : video.volume);

        // IMA never re-reads the player size on its own, so anything that
        // resizes the player later (MRC-pin layout, the runner's resize
        // action) leaves the ad chrome laid out for the old geometry and the
        // control bar ends up covering the Skip Ad button. See adSizing.js.
        adSizeSync = createAdSizeSync({
          element: adContainer,
          getAdsManager: () => adsManager,
          viewMode: google.ima.ViewMode.NORMAL,
          initialSize,
          observe: browserObserver(),
          log: logDiag,
        });
        adSizeSync.syncNow();
        adSizeSync.start();
      } catch (err) {
        reportAdLoadFailure(`adsManager.start failed: ${err.message}`);
        badgePhase.adEnded();
        playback.playContent();
      }
    },
    false,
  );

  adsLoader.addEventListener(
    google.ima.AdErrorEvent.Type.AD_ERROR,
    (evt) => {
      const errMsg = evt.getError().toString();
      reportAdLoadFailure(errMsg);
      hideLoadingOverlay(`Ad Error: ${errMsg.slice(0, 120)}`);
      // IMA is not guaranteed to fire CONTENT_RESUME_REQUESTED after an error
      // it raises mid-break, so end the ad phase here rather than relying on it.
      badgePhase.adEnded();
      playback.playContent();
    },
    false,
  );

  // Kick things off: prepare video, request ads on a user-gesture
  // substitute (Playwright auto-plays because we pass
  // --autoplay-policy=no-user-gesture-required on chromium).
  video.src = config.content;
  video.autoplay = config.autoplay;
  video.muted = config.muted;

  adDisplayContainer.initialize();

  const adsRequest = new google.ima.AdsRequest();
  adsRequest.adTagUrl = withFreshRandomCachebuster(config.vast);
  adsRequest.linearAdSlotWidth = video.clientWidth || 640;
  adsRequest.linearAdSlotHeight = video.clientHeight || 360;
  adsRequest.nonLinearAdSlotWidth = video.clientWidth || 640;
  adsRequest.nonLinearAdSlotHeight = Math.round((video.clientHeight || 360) / 3);

  // Set FULL access so custom vendors (SmartClip) can access geometry for viewability.
  if (window.google?.ima?.OmidVerificationVendor && window.google?.ima?.OmidAccessMode) {
    /** @type {Record<number, string>} */
    const omidRules = {};
    omidRules[google.ima.OmidVerificationVendor.OTHER] = google.ima.OmidAccessMode.FULL;
    adsRequest.omidAccessModeRules = omidRules;
    logDiag('OMID: omidAccessModeRules set → OTHER=FULL (SmartClip/realytics)');
  }

  adsLoader.requestAds(adsRequest);

  // Expose skip/pause handles for deterministic Playwright actions.
  // Also expose MRC viewability data when fixed-position pinning is active
  // (window.__CESP_VIEWABILITY__ is set by applyFixedPositioning).
  window.__CESP_TEST_APP__ = {
    config,
    video,
    adsLoader,
    // Set to true by the IMA SKIPPED listener. PlaywrightRunner reads this
    // after its skip-click loop to confirm the skip actually landed.
    adSkipped: false,
    // Set to true by the IMA STARTED listener. PlaywrightRunner polls this
    // in the skip handler (Phase 0) to wait until the ad is actually
    // rendering before starting the skip-button polling loop.
    adStarted: false,
    // Set by AD_ERROR / adsManager.start failure so the runner stops waiting.
    adError: false,
    adErrorMessage: null,
    get adsManager() {
      return adsManager;
    },
    /**
     * Skip-state bridge required by PlaywrightRunner: it gates its entire
     * skip-click loop on this returning true. Guarded because IMA only
     * exposes getAdSkippableState once an ad is actually rendering.
     */
    isSkippable() {
      try {
        return adsManager?.getAdSkippableState?.() ?? false;
      } catch {
        return false;
      }
    },
    /**
     * When `?visible` param is set, returns the post-pin BoundingClientRect
     * and the measured visible fraction for Playwright assertions (CESP-686).
     * Returns null when running in scroll-based layout mode.
     */
    get viewability() {
      return window.__CESP_VIEWABILITY__ ?? null;
    },
    skipAd() {
      try {
        adsManager?.skip();
      } catch (e) {
        logDiag(`skipAd failed: ${e.message}`);
      }
    },
    pauseAd() {
      adsManager?.pause();
    },
    resumeAd() {
      adsManager?.resume();
    },
    /**
     * Mute state for whichever surface is on screen. PlaywrightRunner calls
     * this for scenario mute/unmute so IMA linear breaks hear setVolume().
     */
    setMuted(muted) {
      applyMuted(muted);
    },
    /**
     * Player volume for whichever surface is on screen, in the 0..1 scale
     * OM SDK reports. PlaywrightRunner calls this for the Mute/Unmute
     * scenario step, once for an immediate transition and once per ramp
     * step for a gradual one (CESP-964).
     */
    setVolume(level) {
      applyVolume(level);
    },
    /**
     * Deterministic ad click-through hook used by PlaywrightRunner.
     * IMA renders the CTA inside its own shadow DOM, so we fire IMA's
     * `clickThrough` API rather than chasing a selector. Falls back to
     * dispatching a click on the ad container so other ad SDKs still
     * record the engagement.
     */
    clickThroughAd(zone) {
      try {
        const w = window;
        if (w.google?.ima && adsManager) {
          // IMA exposes `clickThrough` on the AdsManager in v3.500+.
          if (typeof adsManager.clickThrough === 'function') {
            adsManager.clickThrough();
            return;
          }
        }
        const container = document.getElementById('ad-container');
        container?.dispatchEvent(new MouseEvent('click', { bubbles: true }));
        logDiag(`clickThroughAd dispatched zone=${zone}`);
      } catch (e) {
        logDiag(`clickThroughAd failed: ${e.message}`);
      }
    },
  };
}

function logDiag(line) {
  const el = document.getElementById('diag');
  if (!el) return;
  el.textContent += `${el.textContent ? '\n' : ''}[${new Date().toISOString()}] ${line}`;
}

function reportAdLoadFailure(message) {
  const detail = String(message ?? '').trim() || 'IMA ad load failed (no detail from SDK)';
  logDiag(`AD_ERROR: ${detail}`);
  console.error(`[CESP] AD_ERROR: ${detail}`);
  if (window.__CESP_TEST_APP__) {
    window.__CESP_TEST_APP__.adError = true;
    window.__CESP_TEST_APP__.adErrorMessage = detail;
  }
}

/**
 * Public init. Exported so a Tizen wrapper (or a host page embedding via
 * iframe) can drive the same flow without URL params.
 */
export function initTestApp(override) {
  const config = { ...readConfig(), ...(override ?? {}) };
  renderLayout(config);
  applyBloat(config.bloat);
  setupCmp(config.consent);
  setupImaSdk(config);
  logDiag(`Test app ready. platform=${navigator.userAgent}`);

  // Manual bootstrap for Tizen/non-IMA environments.
  if (!window.google?.ima) {
    const video = document.getElementById('content-video');
    const adContainer = document.getElementById('ad-container');
    if (video && adContainer) {
      bootstrapOmsdk({ video, adContainer, verifications: [] }).catch((err) => {
        logDiag(`Non-IMA OMID bootstrap failed: ${err.message}`);
      });
    }
  }
}

// Auto-init on load unless hosting environment opts out.
if (!window.__CESP_MANUAL_INIT__) {
  initTestApp();
}
